/**
 * BiomePalette - Handles biome color definitions and time-of-day color management
 *
 * Extracted from BiomeRenderer for better separation of concerns.
 * Manages color palettes for rocks, shrubs, terrain, and other biome elements
 * across different times of day (day, sunset, night, dawn).
 */

export class BiomePalette {
    constructor(biomeName = 'Desert') {
        this.biomeName = biomeName;

        // Current time index (0=day, 1=sunset, 2=night, 3=dawn)
        this.currentTimeIndex = 0;
        this.currentPalette = null;

        // Biome-specific colors (set by subclasses or configuration)
        this.biomeColors = null;

        // Cached color values for performance
        this._lastRockColor = undefined;
        this._lastShrubColor = undefined;
        this._lastVegetationEmissive = 0.3;
    }

    /**
     * Set the current palette and time index
     * @param {Object} palette - Palette object with color definitions
     * @param {number} timeIndex - Time index (0=day, 1=sunset, 2=night, 3=dawn)
     */
    setPalette(palette, timeIndex = 0) {
        this.currentPalette = palette;
        this.currentTimeIndex = timeIndex;

        // Update vegetation emissive intensity so newly spawned shrubs use correct value
        if (palette && palette.vegetationEmissiveIntensity !== undefined) {
            this._lastVegetationEmissive = palette.vegetationEmissiveIntensity;
        }
    }

    /**
     * Set biome-specific color definitions
     * @param {Array} colors - Array of color objects per time of day
     */
    setBiomeColors(colors) {
        this.biomeColors = colors;
    }

    /**
     * Get rock colors for the current time of day
     * Uses biome-specific colors if available, falls back to defaults
     * @returns {Array<number>} Array of hex color values
     */
    getCurrentRockColors() {
        if (this.biomeColors && this.biomeColors[this.currentTimeIndex]) {
            return this.biomeColors[this.currentTimeIndex].rockColors || [];
        }
        // Fallback
        return [0x8b8378, 0xa09080, 0x9c8c7c, 0x7a7068];
    }

    /**
     * Get shrub colors for the current time of day
     * Uses biome-specific colors if available, falls back to defaults
     * @returns {Array<number>} Array of hex color values
     */
    getCurrentShrubColors() {
        if (this.biomeColors && this.biomeColors[this.currentTimeIndex]) {
            return this.biomeColors[this.currentTimeIndex].shrubColors || [];
        }
        // Fallback
        return [0x6b7e4e, 0x7d7a3d, 0x8a8b5a, 0x8b7d5a];
    }

    /**
     * Get current vegetation emissive intensity
     * @returns {number} Emissive intensity value
     */
    getVegetationEmissiveIntensity() {
        return this._lastVegetationEmissive;
    }

    /**
     * Check if colors have changed since last update
     * @param {number} rockColor - Rock color to check
     * @param {number} shrubColor - Shrub color to check
     * @param {number} vegetationEmissive - Vegetation emissive to check
     * @returns {boolean} True if any color has changed
     */
    hasColorsChanged(rockColor, shrubColor, vegetationEmissive) {
        return this._lastRockColor !== rockColor ||
               this._lastShrubColor !== shrubColor ||
               this._lastVegetationEmissive !== vegetationEmissive;
    }

    /**
     * Update cached color values
     * @param {number} rockColor - New rock color
     * @param {number} shrubColor - New shrub color
     * @param {number} vegetationEmissive - New vegetation emissive intensity
     */
    updateCachedColors(rockColor, shrubColor, vegetationEmissive) {
        this._lastRockColor = rockColor;
        this._lastShrubColor = shrubColor;
        this._lastVegetationEmissive = vegetationEmissive;
    }

    /**
     * Get a random color from the current rock palette
     * @returns {number} Random rock color hex value
     */
    getRandomRockColor() {
        const colors = this.getCurrentRockColors();
        if (colors.length === 0) return 0x888888;
        return colors[Math.floor(Math.random() * colors.length)];
    }

    /**
     * Get a random color from the current shrub palette
     * @returns {number} Random shrub color hex value
     */
    getRandomShrubColor() {
        const colors = this.getCurrentShrubColors();
        if (colors.length === 0) return 0x228822;
        return colors[Math.floor(Math.random() * colors.length)];
    }

    /**
     * Interpolate between two hex colors
     * @param {number} color1 - First color
     * @param {number} color2 - Second color
     * @param {number} t - Interpolation factor (0-1)
     * @returns {number} Interpolated hex color
     */
    static lerpColor(color1, color2, t) {
        const r1 = (color1 >> 16) & 0xff;
        const g1 = (color1 >> 8) & 0xff;
        const b1 = color1 & 0xff;

        const r2 = (color2 >> 16) & 0xff;
        const g2 = (color2 >> 8) & 0xff;
        const b2 = color2 & 0xff;

        const r = Math.round(r1 + (r2 - r1) * t);
        const g = Math.round(g1 + (g2 - g1) * t);
        const b = Math.round(b1 + (b2 - b1) * t);

        return (r << 16) | (g << 8) | b;
    }
}
